import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../api/axios';
import Loading from '../components/Loading';
import { Transaction } from '../types';
import AddManualTransaction from './AddManualTransaction';
import RegisterRedemption from './RegisterRedemption';
import './Pages.css';

type TransactionFilter = 'pending' | 'approved' | 'rejected' | 'redemptions';

const Transactions: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<TransactionFilter>('pending');
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [processingTransactionId, setProcessingTransactionId] = useState<string | null>(null);
  const [showAddTransaction, setShowAddTransaction] = useState(false);
  const [showRegisterRedemption, setShowRegisterRedemption] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    loadTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter]);

  const loadTransactions = async () => {
    setLoading(true);
    try {
      let endpoint = '/admin/transactions/pending';
      if (activeFilter === 'approved') {
        endpoint = '/admin/transactions/approved';
      } else if (activeFilter === 'rejected') {
        endpoint = '/admin/transactions/rejected';
      } else if (activeFilter === 'redemptions') {
        endpoint = '/admin/transactions/redemptions';
      }
      
      const response = await api.get<Transaction[]>(endpoint);
      setTransactions(response.data);
    } catch (error) {
      console.error('Error loading transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (transactionId: string) => {
    if (processingTransactionId) return;
    
    setProcessingTransactionId(transactionId);
    try {
      await api.post(`/admin/transactions/${transactionId}/approve`);
      loadTransactions();
    } catch (error) {
      console.error('Error approving transaction:', error);
      alert('Erro ao aprovar transação');
    } finally {
      setProcessingTransactionId(null);
    }
  };

  const handleReject = async (transactionId: string) => {
    if (processingTransactionId) return;
    
    const reason = prompt('Motivo da rejeição:');
    if (!reason) return;

    setProcessingTransactionId(transactionId);
    try {
      await api.post(`/admin/transactions/${transactionId}/reject`, { transactionId, reason });
      loadTransactions();
    } catch (error) {
      console.error('Error rejecting transaction:', error);
      alert('Erro ao rejeitar transação');
    } finally {
      setProcessingTransactionId(null);
    }
  };

  const getFilterTitle = () => {
    switch (activeFilter) {
      case 'pending':
        return 'Transações Pendentes';
      case 'approved':
        return 'Transações Aprovadas';
      case 'rejected':
        return 'Transações Rejeitadas';
      case 'redemptions':
        return 'Resgates';
      default:
        return 'Transações';
    }
  };

  const getFilterDescription = () => {
    switch (activeFilter) {
      case 'pending':
        return 'Aprove ou rejeite pontuações de clientes';
      case 'approved':
        return 'Transações aprovadas e processadas';
      case 'rejected':
        return 'Transações rejeitadas';
      case 'redemptions':
        return 'Histórico de resgates realizados';
      default:
        return '';
    }
  };

  const getEmptyStateMessage = () => {
    switch (activeFilter) {
      case 'pending':
        return { title: 'Nenhuma transação pendente', message: 'Todas as transações foram processadas' };
      case 'approved':
        return { title: 'Nenhuma transação aprovada', message: 'Ainda não há transações aprovadas' };
      case 'rejected':
        return { title: 'Nenhuma transação rejeitada', message: 'Ainda não há transações rejeitadas' };
      case 'redemptions':
        return { title: 'Nenhum resgate realizado', message: 'Ainda não há resgates registrados' };
      default:
        return { title: 'Nenhuma transação', message: '' };
    }
  };

  const openImageModal = (imageUrl: string) => {
    setSelectedImage(imageUrl);
    setZoomLevel(1);
    setImagePosition({ x: 0, y: 0 });
  };

  const closeModal = useCallback(() => {
    setSelectedImage(null);
    setZoomLevel(1);
    setImagePosition({ x: 0, y: 0 });
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && selectedImage) {
        closeModal();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage, closeModal]);

  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(prev - 0.25, 0.5));
  };

  const handleResetZoom = () => {
    setZoomLevel(1);
    setImagePosition({ x: 0, y: 0 });
  };

  const handleWheel = (e: React.WheelEvent) => {
    if (!selectedImage) return;
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoomLevel((prev) => Math.max(0.5, Math.min(3, prev + delta)));
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoomLevel > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - imagePosition.x,
        y: e.clientY - imagePosition.y,
      });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && zoomLevel > 1) {
      setImagePosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  if (loading) return <Loading message="Carregando transações..." />;

  const emptyState = getEmptyStateMessage();

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>{getFilterTitle()}</h1>
          <p>{getFilterDescription()}</p>
        </div>
        <div style={{ display: 'flex', gap: '12px', marginLeft: 'auto' }}>
          <button
            className="btn-secondary"
            onClick={() => setShowRegisterRedemption(true)}
          >
            🎁 Registrar Resgate
          </button>
          <button
            className="btn-primary"
            onClick={() => setShowAddTransaction(true)}
          >
            ➕ Adicionar Transação
          </button>
        </div>
      </div>

      <div className="transaction-filters">
        <button
          className={`filter-tab ${activeFilter === 'pending' ? 'active' : ''}`}
          onClick={() => setActiveFilter('pending')}
        >
          Pendentes
        </button>
        <button
          className={`filter-tab ${activeFilter === 'approved' ? 'active' : ''}`}
          onClick={() => setActiveFilter('approved')}
        >
          Aprovadas
        </button>
        <button
          className={`filter-tab ${activeFilter === 'rejected' ? 'active' : ''}`}
          onClick={() => setActiveFilter('rejected')}
        >
          Rejeitadas
        </button>
        <button
          className={`filter-tab ${activeFilter === 'redemptions' ? 'active' : ''}`}
          onClick={() => setActiveFilter('redemptions')}
        >
          🎁 Resgates
        </button>
      </div>

      {transactions.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">✓</div>
          <h3>{emptyState.title}</h3>
          <p>{emptyState.message}</p>
        </div>
      ) : (
        <div className="transactions-table">
          {transactions.map((transaction) => (
            <div key={transaction.transactionId} className="transaction-card">
              <div className="transaction-main">
                <div className="transaction-details">
                  <h3>Transação #{transaction.transactionId.substring(0, 8)}</h3>
                  <div className="detail-row">
                    <span className="label">Data:</span>
                    <span>{new Date(transaction.transactionDate).toLocaleDateString('pt-BR')}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Valor:</span>
                    <span className="amount">R$ {transaction.amount.toFixed(2)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Pontos:</span>
                    <span className="points">{transaction.pointsEarned} pts</span>
                  </div>
                  {transaction.clientName && (
                    <>
                      <div className="detail-row">
                        <span className="label">Cliente:</span>
                        <span>{transaction.clientName}</span>
                      </div>
                      {transaction.clientCpf && (
                        <div className="detail-row">
                          <span className="label">CPF:</span>
                          <span>{transaction.clientCpf}</span>
                        </div>
                      )}
                      {transaction.clientPhone && (
                        <div className="detail-row">
                          <span className="label">Telefone:</span>
                          <span>{transaction.clientPhone}</span>
                        </div>
                      )}
                    </>
                  )}
                  {transaction.notes && (
                    <div className="detail-row">
                      <span className="label">Observações:</span>
                      <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{transaction.notes}</span>
                    </div>
                  )}
                  {transaction.historyLog && transaction.historyLog.length > 0 && (
                    <div className="detail-row" style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e0e0e0' }}>
                      <div style={{ width: '100%' }}>
                        <span className="label" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                          Histórico:
                        </span>
                        <ul style={{ 
                          listStyle: 'none', 
                          padding: 0, 
                          margin: 0,
                          fontSize: '13px',
                          color: '#666',
                          maxHeight: '150px',
                          overflowY: 'auto'
                        }}>
                          {transaction.historyLog.map((logEntry, index) => (
                            <li key={index} style={{ 
                              padding: '6px 0',
                              borderBottom: index < transaction.historyLog!.length - 1 ? '1px solid #f0f0f0' : 'none'
                            }}>
                              {logEntry}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
                {transaction.receiptPhotoUrl && (
                  <div className="receipt-preview">
                    <img 
                      src={transaction.receiptPhotoUrl} 
                      alt="Nota fiscal" 
                      onClick={() => openImageModal(transaction.receiptPhotoUrl!)}
                    />
                  </div>
                )}
              </div>
              {activeFilter === 'pending' && (
                <div className="transaction-actions">
                  <button
                    className="btn-approve"
                    onClick={() => handleApprove(transaction.transactionId)}
                    disabled={processingTransactionId === transaction.transactionId}
                  >
                    {processingTransactionId === transaction.transactionId ? 'Processando...' : '✓ Aprovar'}
                  </button>
                  <button
                    className="btn-reject"
                    onClick={() => handleReject(transaction.transactionId)}
                    disabled={processingTransactionId === transaction.transactionId}
                  >
                    {processingTransactionId === transaction.transactionId ? 'Processando...' : '✗ Rejeitar'}
                  </button>
                </div>
              )}
              {activeFilter === 'approved' && transaction.approvedAt && (
                <div className="transaction-status-info">
                  <div className="status-badge approved">
                    ✓ Aprovada em {new Date(transaction.approvedAt).toLocaleDateString('pt-BR')}
                  </div>
                </div>
              )}
              {activeFilter === 'rejected' && transaction.rejectionReason && (
                <div className="transaction-status-info">
                  <div className="status-badge rejected">
                    ✗ Rejeitada
                  </div>
                  <div className="rejection-reason">
                    <strong>Motivo:</strong> {transaction.rejectionReason}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {selectedImage && (
        <div 
          className="image-modal-overlay" 
          onClick={closeModal}
          onWheel={handleWheel}
        >
          <div 
            className="image-modal-content"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            <div className="image-modal-header">
              <h3>Nota Fiscal</h3>
              <button className="image-modal-close" onClick={closeModal}>
                ✕
              </button>
            </div>
            <div className="image-modal-zoom-controls">
              <button onClick={handleZoomOut} disabled={zoomLevel <= 0.5}>
                −
              </button>
              <span>{Math.round(zoomLevel * 100)}%</span>
              <button onClick={handleZoomIn} disabled={zoomLevel >= 3}>
                +
              </button>
              <button onClick={handleResetZoom}>Reset</button>
            </div>
            <div className="image-modal-container">
              <img
                ref={imageRef}
                src={selectedImage}
                alt="Nota fiscal ampliada"
                style={{
                  transform: `translate(${imagePosition.x}px, ${imagePosition.y}px) scale(${zoomLevel})`,
                  cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
                }}
              />
            </div>
          </div>
        </div>
      )}

      {showAddTransaction && (
        <AddManualTransaction
          onClose={() => setShowAddTransaction(false)}
          onSuccess={() => {
            loadTransactions();
            setShowAddTransaction(false);
          }}
        />
      )}

      {showRegisterRedemption && (
        <RegisterRedemption
          onClose={() => setShowRegisterRedemption(false)}
          onSuccess={() => {
            loadTransactions();
            setShowRegisterRedemption(false);
          }}
        />
      )}
    </div>
  );
};

export default Transactions;


